import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { CheckCircle, Loader2, XCircle } from 'lucide-react';

const LOGO_URL = "/images/area-logo.png";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RegisterSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [error, setError] = useState('');
  const [userData, setUserData] = useState(null);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    
    if (!sessionId) {
      setStatus('error');
      setError('No payment session found. Please try registering again.');
      return;
    }
    
    verifyAndComplete(sessionId);
  }, [searchParams]);

  const verifyAndComplete = async (sessionId) => {
    try {
      // First, check payment status
      const statusResponse = await fetch(`${API}/payment/status/${sessionId}`);
      if (!statusResponse.ok) {
        throw new Error('Failed to verify payment status');
      }
      
      const statusData = await statusResponse.json();
      
      if (statusData.payment_status !== 'paid') {
        setStatus('error');
        setError('Payment was not completed. Please try again.');
        return;
      }
      
      // Payment successful - now complete registration with stored data
      if (statusData.registration_data) {
        const regData = statusData.registration_data;
        
        const registerResponse = await fetch(`${API}/auth/register-with-payment?session_id=${sessionId}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            email: regData.email,
            password: regData.password,
            first_name: regData.first_name,
            last_name: regData.last_name,
            phone: regData.phone,
            user_role: regData.user_role,
            license_number: regData.license_number || null,
            license_states: regData.license_states || [],
            brokerage_name: regData.brokerage_name,
            brokerage_city: regData.brokerage_city,
            brokerage_state: regData.brokerage_state,
            areas_of_focus: regData.areas_of_focus || [],
            membership_tier: regData.membership_tier
          })
        });
        
        if (registerResponse.ok) {
          const result = await registerResponse.json();
          // Store session
          document.cookie = `session_token=${result.session_token}; path=/; max-age=${7 * 24 * 60 * 60}`;
          setUser(result.user);
          setUserData(result.user);
          setStatus('success');
          toast.success('Registration successful! Welcome to AREA.');
        } else {
          const errorData = await registerResponse.json();
          // Check if user already exists (e.g., page refresh)
          if (errorData.detail?.includes('already registered')) {
            setStatus('success');
            setUserData({ membership_tier: regData.membership_tier });
            toast.info('Your account has already been created. Please login.');
          } else {
            throw new Error(errorData.detail || 'Registration failed');
          }
        }
      } else {
        setStatus('error');
        setError('Registration data not found. Please contact support.');
      }
    } catch (err) {
      console.error('Registration error:', err);
      setStatus('error');
      setError(err.message || 'An error occurred during registration');
    }
  };

  const getTierName = (tier) => {
    const names = {
      basic: 'Basic',
      select: 'Select',
      founder: 'Founder'
    };
    return names[tier] || tier;
  };

  return (
    <div className="min-h-screen bg-slate-50 py-12 px-4" data-testid="register-success-page">
      <div className="w-full max-w-lg mx-auto">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/">
            <img 
              src={LOGO_URL} 
              alt="AREA" 
              className="h-16 w-auto mx-auto mb-4"
            />
          </Link>
        </div>

        <Card className="border-0 shadow-xl">
          <CardContent className="p-8 text-center">
            {status === 'verifying' && (
              <div className="space-y-4">
                <Loader2 className="w-16 h-16 text-[#3D4F7A] mx-auto animate-spin" />
                <h2 className="font-heading text-xl font-bold text-slate-900">
                  Verifying Your Payment...
                </h2>
                <p className="text-slate-600">
                  Please wait while we confirm your payment and set up your account.
                </p>
              </div>
            )}

            {status === 'success' && (
              <div className="space-y-6">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                  <CheckCircle className="w-12 h-12 text-green-600" />
                </div>
                <div>
                  <h2 className="font-heading text-2xl font-bold text-slate-900 mb-2">
                    Welcome to AREA!
                  </h2>
                  <p className="text-slate-600">
                    Your {userData?.membership_tier && getTierName(userData.membership_tier)} membership has been activated.
                  </p>
                </div>
                
                <div className="bg-slate-50 rounded-lg p-4 text-left space-y-2">
                  <h3 className="font-semibold text-slate-800">What's Next?</h3>
                  <ul className="text-sm text-slate-600 space-y-1">
                    <li>• Complete your profile to connect with other members</li>
                    <li>• Explore the member directory</li>
                    <li>• Join groups and participate in discussions</li>
                    <li>• Access exclusive resources and events</li>
                  </ul>
                </div>

                <div className="space-y-3">
                  <Button 
                    onClick={() => navigate('/dashboard')}
                    className="w-full h-12 bg-[#3D4F7A] hover:bg-[#2A3755]"
                    data-testid="go-to-dashboard"
                  >
                    Go to Dashboard
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => navigate('/community')}
                    className="w-full h-12"
                  >
                    Explore Community
                  </Button>
                </div>
              </div>
            )}

            {status === 'error' && (
              <div className="space-y-6">
                <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto">
                  <XCircle className="w-12 h-12 text-red-600" />
                </div>
                <div>
                  <h2 className="font-heading text-2xl font-bold text-slate-900 mb-2">
                    Registration Issue
                  </h2>
                  <p className="text-slate-600">
                    {error}
                  </p>
                </div>

                <div className="space-y-3">
                  <Button 
                    onClick={() => navigate('/register')}
                    className="w-full h-12 bg-[#3D4F7A] hover:bg-[#2A3755]"
                  >
                    Try Again
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => navigate('/contact')}
                    className="w-full h-12"
                  >
                    Contact Support
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <p className="text-center text-xs text-slate-500 mt-8">
          Need help?{' '}
          <Link to="/contact" className="underline">Contact our support team</Link>
        </p>
      </div>
    </div>
  );
}
